import { safeLocalStorage } from 'helpers/localStorage'

/**
 * What the customer chose. `system` follows the operating system and is the
 * default, so Morta matches the rest of their desktop until they say otherwise.
 */
export type ThemePreference = 'light' | 'dark' | 'system'

/** What we actually paint. `system` has been resolved away by this point. */
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'morta-theme'

/**
 * The class that carries the dark palette, set on <html>.
 *
 * The inline script in index.html writes this before first paint — keep the two
 * in step. It is duplicated there on purpose: importing this module would mean
 * waiting for the bundle, and the whole point is to beat the first paint.
 */
export const DARK_CLASS = 'dark'

const PREFERENCES: ThemePreference[] = ['light', 'dark', 'system']

export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

export const isThemePreference = (value: unknown): value is ThemePreference =>
  typeof value === 'string' && (PREFERENCES as string[]).includes(value)

/**
 * The stored preference, or `system` when nothing is stored — which is also what
 * a blocked or cleared localStorage gives us, and is the right thing to fall
 * back to either way.
 */
export const readStoredTheme = (): ThemePreference => {
  const stored = safeLocalStorage.getItem(THEME_STORAGE_KEY)
  return isThemePreference(stored) ? stored : 'system'
}

export const storeTheme = (preference: ThemePreference): void => {
  safeLocalStorage.setItem(THEME_STORAGE_KEY, preference)
}

/** What the operating system is asking for. Assumes light where unsupported. */
export const getSystemTheme = (): ResolvedTheme =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(DARK_MEDIA_QUERY).matches
    ? 'dark'
    : 'light'

export const resolveTheme = (preference: ThemePreference): ResolvedTheme =>
  preference === 'system' ? getSystemTheme() : preference

/**
 * Put the theme on the document.
 *
 * `color-scheme` is the part people forget: without it the browser keeps
 * painting form controls, scrollbars and the page behind an overscroll bounce in
 * light colours, which shows up as white flashes at the edges of a dark app. The
 * theme-color meta follows so mobile browser chrome matches too.
 */
export const applyTheme = (theme: ResolvedTheme): void => {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.classList.toggle(DARK_CLASS, theme === 'dark')
  root.style.colorScheme = theme

  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#16181c' : '#ffffff')
}

/**
 * Resolve a colour token to a concrete value.
 *
 * Needed for anything that paints outside CSS — chart.js writes its colours to
 * `ctx.fillStyle` on a canvas, and a canvas cannot resolve `var(--color-…)`.
 * Assigning one is silently ignored, so the chart quietly keeps whatever colour
 * it had rather than the token, which looks fine on the light theme and wrong on
 * the dark one.
 *
 * Callers must re-read this when the theme changes — take `useResolvedTheme()`
 * so the component re-renders, rather than caching the result at module scope.
 */
export const readTokenColour = (token: string, fallback = '#333333'): string => {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') return fallback
  return getComputedStyle(document.documentElement).getPropertyValue(token).trim() || fallback
}
